import { useEffect, useState } from "react";
import api from '../../services/apiClient.ts';
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Loader, Plus, Pencil, Trash2 } from "lucide-react";
import Modal from "../common/Modal";

export default function SupervisorsPage() {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);

  // حذف بالتأكيد عبر مودال
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [form, setForm] = useState({
    name: "",
    opening_balance: "",
  });

  const headersAuth = {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    "X-Tenant": localStorage.getItem("tenant_key"),
    "X-Branch": localStorage.getItem("branch_id"),
  };  

  // ================== تحميل البيانات ==================
  const loadData = async () => {
    try {
      setLoading(true);

      const resp = await api.get("/v1/supervisors", {
        headers: headersAuth,
      });

      setItems(resp.data || []);
    } catch (err) {
      console.error(err);
      toast.error("فشل في تحميل المشرفين");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // ================== فتح مودال إضافة ==================
  const openAdd = () => {
    setEditItem(null);
    setForm({
      name: "",
      opening_balance: "",
    });
    setModalOpen(true);
  };

  // ================== فتح مودال تعديل ==================
  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      name: item.name || "",
      opening_balance: item.opening_balance ?? "",
    });
    setModalOpen(true);
  };

  // ================== حفظ (إضافة / تعديل) ==================
  const save = async () => {
    if (saving) return; // منع التكرار
    if (!form.name.trim()) {
      return toast.error("أدخل اسم المشرف");
    }

    const payload = {
      name: form.name,
      // نخليها null إذا فاضية حتى ياخذها الباك اند 0
      opening_balance:
        form.opening_balance === "" ? null : Number(form.opening_balance),
    };

    try {
      setSaving(true);
      if (editItem) {
        // تعديل
        await api.put(`/v1/supervisors/${editItem.id}`, payload, {
          headers: headersAuth,
        });
        toast.success("تم تعديل المشرف");
      } else {
        // إضافة
        await api.post("/v1/supervisors", payload, {
          headers: headersAuth,
        });
        toast.success("تمت إضافة المشرف");
      }

      setModalOpen(false);
      await loadData();
    } catch (err) {
      console.error(err);
      if (err.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error("حدث خطأ أثناء الحفظ");
      }
    } finally {
      setSaving(false);
    }
  };

  // ================== تأكيد الحذف عبر مودال ==================
  const openDeleteConfirm = (id) => {
    setConfirmDeleteId(id);
    setConfirmOpen(true);
  };

  const performDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      setDeleting(true);
      await api.delete(`/v1/supervisors/${confirmDeleteId}`, { headers: headersAuth });
      toast.success("تم حذف المشرف");
      setConfirmOpen(false);
      setConfirmDeleteId(null);
      await loadData();
    } catch (err) {
      console.error(err);
      toast.error("لا يمكن حذف هذا المشرف");
    } finally {
      setDeleting(false);
    }
  };

  // ================== UI ==================
  return (
    <div className="w-full mx-auto p-4 sm:p-6">
      <div className="flex items-center justify-between mb-6 rtl:flex-row-reverse" dir={i18n.dir()}>
        <div>
          <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">{t("supervisors.title")}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">{t("supervisors.subtitle")}</p>
        </div>
        <button
          className="inline-flex items-center gap-2 rounded-md bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 shadow-sm"
          onClick={openAdd}
        >
          <Plus className="h-4 w-4" />
          {t("supervisors.addSupervisor")}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-[40vh]">
          <div className="flex items-center gap-3 text-gray-700 dark:text-gray-200">
            <Loader className="h-6 w-6 animate-spin" />
            <span className="text-lg font-semibold">{t("common.loading")}</span>
          </div>
        </div>
      ) : (
        <div className="app-table-wrap shadow-sm">
          <table className="app-table min-w-full">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-right rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400">#</th>
                <th className="px-4 py-3 text-right rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400">الاسم</th>
                <th className="px-4 py-3 text-right rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400">الرصيد الافتتاحي</th>
                <th className="px-4 py-3 text-right rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400">{t("common.actions")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
              {items.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">{t("common.noData")}</td>
                </tr>
              )}

              {items.map((e, i) => (
                <tr key={e.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-4 py-3 text-right rtl:text-right text-sm text-gray-700 dark:text-gray-200">{i + 1}</td>
                  <td className="px-4 py-3 text-right rtl:text-right text-sm text-gray-900 dark:text-gray-100">{e.name}</td>
                  <td className="px-4 py-3 text-right rtl:text-right text-sm text-gray-700 dark:text-gray-200">
                    {e.opening_balance != null ? Number(e.opening_balance).toLocaleString() : "0"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center gap-2 rtl:flex-row-reverse justify-start ">
                      <button
                        className="inline-flex items-center gap-1 rounded-md bg-primary-500 hover:bg-primary-600 text-white px-3 py-1.5"
                        onClick={() => openEdit(e)}
                      >
                        <Pencil className="h-4 w-4" />
                        {t("common.edit")}
                      </button>
                      <button
                        className="inline-flex items-center gap-1 rounded-md bg-red-600 hover:bg-red-700 text-white px-3 py-1.5"
                        type="button"
                        onClick={() => openDeleteConfirm(e.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                        {t("common.delete")}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        show={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editItem ? t("supervisors.editSupervisor") : t("supervisors.addSupervisor")}
      >
        <div className="space-y-3">
          <div>
            <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">اسم المشرف</label>
            <input
              type="text"
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </div>

          <div>
            <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">الرصيد الافتتاحي</label>
            <input
              type="number"
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              value={form.opening_balance}
              onChange={(e) => setForm((f) => ({ ...f, opening_balance: e.target.value }))}
              min="0"
              step="0.01"
            />
          </div>

          <div className="flex items-center justify-end gap-2 rtl:flex-row-reverse pt-2">
            <button
              className="rounded-md bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 px-4 py-2 border border-gray-200 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700"
              type="button"
              onClick={() => setModalOpen(false)}
            >
              {t("common.cancel")}
            </button>
            <button
              className="rounded-md bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2"
              type="button"
              onClick={save}
              disabled={saving}
            >
              {saving && <Loader className="h-4 w-4 animate-spin" />}
              {t("common.save")}
            </button>
          </div>
        </div>
      </Modal>

      {/* مودال تأكيد الحذف */}
      <Modal
        show={confirmOpen}
        onClose={() => { if (!deleting) { setConfirmOpen(false); setConfirmDeleteId(null); } }}
        title={t("common.confirm")}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700 dark:text-gray-200">هل تريد حذف هذا المشرف؟ لا يمكن التراجع عن هذه العملية.</p>
          <div className="flex items-center justify-end gap-2 rtl:flex-row-reverse">
            <button
              className="rounded-md bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 px-4 py-2 border border-gray-200 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700"
              type="button"
              onClick={() => { if (!deleting) { setConfirmOpen(false); setConfirmDeleteId(null); } }}
            >
              {t("common.cancel")}
            </button>
            <button
              className="rounded-md bg-red-600 hover:bg-red-700 text-white px-4 py-2 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2"
              type="button"
              onClick={performDelete}
              disabled={deleting}
            >
              {deleting && <Loader className="h-4 w-4 animate-spin" />}
              {t("common.delete")}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
